import { useEffect, useMemo, useRef, useState } from 'react';
import { PrinterOutlined } from '@ant-design/icons';
import { Button, Card, Empty, QRCode, Select, Spin, Typography } from 'antd';
import { useReactToPrint } from 'react-to-print';

import LayoutWithMenu from 'components/Layout/LayoutWithMenu';
import { post } from 'core/fetch';

const { Text } = Typography;

const COMPANY_LOGO_URL =
  'https://oyu-dot-consulting.s3.eu-central-1.amazonaws.com/logo/shineunuudur-logo.png';
const QR_SIZE = 160;
// LOGO_SIZE must match the size-9 (36px) class on the overlay logo image.
const LOGO_SIZE = 36;
const ROWS = 2;
const COLS = 5;
// Keep in sync with the grid-cols-5 / grid-rows-2 classes on the page element.
const CARDS_PER_PAGE = ROWS * COLS;

interface TeacherMealQr {
  id: number;
  code: string;
  full_name: string;
  qr_value: string;
}

const TeacherMealQrPrinting = () => {
  const [teachers, setTeachers] = useState<TeacherMealQr[]>([]);
  const [teachersLoading, setTeachersLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  useEffect(() => {
    setTeachersLoading(true);
    post<{ items: TeacherMealQr[] }>('/custom/teacher/meal-qr', {})
      .then(response => setTeachers(response.items))
      .finally(() => setTeachersLoading(false));
  }, []);

  const printRef = useRef<HTMLDivElement>(null);
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: 'teacher-meal-qr',
    pageStyle: '@page { size: A4 landscape; margin: 0; }',
  });

  const sortedTeachers = useMemo(
    () =>
      [...teachers].sort((a, b) =>
        a.full_name.localeCompare(b.full_name, 'mn'),
      ),
    [teachers],
  );

  const pages = useMemo(() => {
    const chosen =
      selectedIds.length === 0
        ? sortedTeachers
        : sortedTeachers.filter(teacher => selectedIds.includes(teacher.id));
    const result: TeacherMealQr[][] = [];
    for (let i = 0; i < chosen.length; i += CARDS_PER_PAGE) {
      result.push(chosen.slice(i, i + CARDS_PER_PAGE));
    }
    return result;
  }, [sortedTeachers, selectedIds]);

  return (
    <LayoutWithMenu className="px-3 lg:px-5">
      <Card>
        <div className="flex flex-wrap gap-3 mb-6">
          <Select
            mode="multiple"
            allowClear
            showSearch={{ optionFilterProp: 'label' }}
            maxTagCount="responsive"
            placeholder="Бүх багш (сонгохгүй бол бүгд)"
            className="min-w-80 max-w-140"
            disabled={teachers.length === 0}
            value={selectedIds}
            onChange={value => setSelectedIds(value)}
            options={sortedTeachers.map(teacher => ({
              value: teacher.id,
              label: teacher.full_name,
            }))}
          />

          <Button
            icon={<PrinterOutlined />}
            disabled={pages.length === 0}
            onClick={() => handlePrint()}
          >
            Хэвлэх
          </Button>
        </div>

        {teachersLoading && <Spin />}

        {!teachersLoading && teachers.length === 0 && (
          <Empty description="Багш алга" />
        )}

        {!teachersLoading && teachers.length > 0 && (
          <div className="overflow-x-auto bg-[#f0f0f0] p-4 print:overflow-visible print:bg-transparent print:p-0">
            <div ref={printRef}>
              {pages.map((pageTeachers, pageIndex) => (
                <div
                  key={pageIndex}
                  className="mx-auto mb-4 box-border grid h-[208mm] w-[297mm] grid-cols-5 grid-rows-2 gap-[2mm] overflow-hidden bg-white p-[8mm] shadow-[0_1px_6px_rgba(0,0,0,0.25)] not-last:print:break-after-page print:m-0 print:shadow-none"
                >
                  {pageTeachers.map(teacher => (
                    <div key={teacher.id} className="min-h-0">
                      <div className="box-border h-full rounded border border-dashed border-[#999] p-1.5 text-center wrap-anywhere">
                        <Text type="secondary" className="block text-[9px]">
                          Шинэ Өнөөдөр Багш
                        </Text>
                        <Text strong className="block mb-1 text-[10px]">
                          {teacher.full_name}
                        </Text>
                        <Text
                          type="secondary"
                          className="block mb-1 text-[8px]"
                        >
                          {teacher.code}
                        </Text>

                        <div className="flex justify-center relative">
                          <QRCode
                            type="svg"
                            value={teacher.qr_value}
                            size={QR_SIZE}
                            errorLevel="H"
                            color="#0b3d0b"
                            bgColor="#fff"
                            bordered={false}
                            icon={COMPANY_LOGO_URL}
                            iconSize={LOGO_SIZE}
                          />
                          <img
                            src={COMPANY_LOGO_URL}
                            alt=""
                            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 size-9 object-contain bg-white rounded-md"
                          />
                        </div>

                        <Text className="block mt-1 text-[9px]">
                          Хоолны бүртгэлийн QR
                        </Text>

                        <Text className="block mt-1 text-[8px] font-semibold text-[#cf1322]">
                          Энэ картыг зөвхөн эзэмшигч ашиглана, бусдад бүү
                          дамжуулна уу.
                        </Text>

                        <div className="mt-1 pt-1 text-[8px] text-[#595959] border-t border-dashed border-[#d9d9d9] text-left leading-normal">
                          Энэ QR кодыг цэцэрлэг/сургуулийн хоолны танхимд
                          уншуулж, багшийн хоолны бүртгэлийг хийнэ.
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>
    </LayoutWithMenu>
  );
};

export default TeacherMealQrPrinting;
